import { useEffect, useState } from 'react';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import {
	Checkbox,
	Form,
	FormButtons,
	FormHeader,
	Input,
	Select,
	SwitchButton,
	Textarea,
	useZodForm,
} from '../../../../../components/FormElements';
import WhiteContainer from '../../../../../components/WhiteContainer/WhiteContainer';
import { Modal } from '../../../../../components/Modal';
import Button from '../../../../../components/Button/Button';
import { api } from '../../../../../services/angular/axios';
import { ToastError } from '../../../../../helpers/errors/ToastError';

interface IntegrationSettingsModel {
	id?: number;
	companyId: number;
	apiEnabled: boolean;
	webhookUrl?: string | null;
	webhookActive?: boolean;
	authToken?: string | null;
	smsCustomText?: string | null;
	externalCompanyId?: number | null;
	ifoodId?: string | null;
	companyCalculatorCostId?: number | null;
}

interface CalculatorCost {
	id: number;
	groupName: string;
}

const settingsSchema = z.object({
	webhookUrl: z
		.string()
		.url('URL inválida')
		.or(z.literal(''))
		.nullish(),
	webhookActive: z.boolean().optional(),
	authToken: z.string().nullish(),
	smsCustomText: z.string().max(160, 'Máximo de 160 caracteres').nullish(),
	externalCompanyId: z.string().nullish(),
	ifoodId: z.string().nullish(),
	companyCalculatorCostId: z
		.object({ value: z.number().nullable(), label: z.string().optional() })
		.nullish(),
});

type SettingsFormType = z.infer<typeof settingsSchema>;

const emptyToNull = (value?: string | null) =>
	value === undefined || value === null || value.trim() === ''
		? null
		: value.trim();

export const IntegrationSettings = ({ companyId }: { companyId?: string }) => {
	const queryClient = useQueryClient();
	const [isSettingsOpen, setIsSettingsOpen] = useState(false);
	const queryKey = ['company-integration', companyId];

	const { data: settings, isFetching } = useQuery(
		queryKey,
		async () => {
			const response = await api.get<IntegrationSettingsModel>(
				`/companies/${companyId ?? ''}/integration`
			);
			return response.data;
		},
		{ enabled: !!companyId }
	);

	const { data: calculatorCosts } = useQuery(
		['integration-calculator-costs'],
		async () => {
			const response = await api.get<CalculatorCost[]>(
				'/companies/integration/calculator-costs'
			);
			return response.data;
		},
		{ enabled: isSettingsOpen }
	);

	const { mutate: toggleApi, isLoading: isToggling } = useMutation(
		async (enabled: boolean) =>
			await api.put<IntegrationSettingsModel>(
				`/companies/${companyId ?? ''}/integration/enabled`,
				{ enabled }
			),
		{
			onSuccess: (response) => {
				queryClient.setQueryData(queryKey, response.data);
				toast.success(
					response.data.apiEnabled
						? 'APIs de integração habilitadas!'
						: 'APIs de integração desabilitadas.'
				);
			},
			onError: ToastError,
		}
	);

	const { mutate: saveSettings, isLoading: isSaving } = useMutation(
		async (payload: Partial<IntegrationSettingsModel>) =>
			await api.put<IntegrationSettingsModel>(
				`/companies/${companyId ?? ''}/integration`,
				payload
			),
		{
			onSuccess: (response) => {
				queryClient.setQueryData(queryKey, response.data);
				toast.success('Configurações de integração salvas!');
				setIsSettingsOpen(false);
			},
			onError: ToastError,
		}
	);

	const form = useZodForm({ schema: settingsSchema });
	const {
		register,
		control,
		reset,
		formState: { errors, isDirty, dirtyFields },
	} = form;

	const calculatorCostOptions = [
		{ value: null, label: 'Nenhuma' },
		...(calculatorCosts ?? []).map((cost) => ({
			value: cost.id,
			label: cost.groupName,
		})),
	];

	useEffect(() => {
		if (!settings || !isSettingsOpen) return;

		const currentCostId = settings.companyCalculatorCostId ?? null;
		const currentCost = calculatorCosts?.find(
			(cost) => cost.id === currentCostId
		);

		reset({
			webhookUrl: settings.webhookUrl ?? '',
			webhookActive: !!settings.webhookActive,
			authToken: settings.authToken ?? '',
			smsCustomText: settings.smsCustomText ?? '',
			externalCompanyId:
				settings.externalCompanyId != null
					? String(settings.externalCompanyId)
					: '',
			ifoodId: settings.ifoodId ?? '',
			// Tabela vinculada que foi deletada não vem na listagem: mantém o vínculo visível
			companyCalculatorCostId: currentCost
				? { value: currentCost.id, label: currentCost.groupName }
				: currentCostId !== null
				? { value: currentCostId, label: `Tabela #${currentCostId} (removida)` }
				: { value: null, label: 'Nenhuma' },
		});
	}, [settings, calculatorCosts, isSettingsOpen]);

	const handleSave = (data: SettingsFormType) => {
		const externalCompanyId = emptyToNull(data.externalCompanyId);

		saveSettings({
			webhookUrl: emptyToNull(data.webhookUrl),
			webhookActive: !!data.webhookActive,
			authToken: emptyToNull(data.authToken),
			smsCustomText: emptyToNull(data.smsCustomText),
			externalCompanyId:
				externalCompanyId !== null ? Number(externalCompanyId) : null,
			ifoodId: emptyToNull(data.ifoodId),
			// Só envia a tabela de preço se a operação alterou o campo
			...(dirtyFields.companyCalculatorCostId && {
				companyCalculatorCostId: data.companyCalculatorCostId?.value ?? null,
			}),
		});
	};

	const apiEnabled = !!settings?.apiEnabled;

	return (
		<WhiteContainer className="flex gap-4">
			<FormHeader
				title="Integração"
				description="Libera o uso das APIs de integração para esta empresa"
			/>
			<SwitchButton
				label={apiEnabled ? 'APIs habilitadas' : 'APIs desabilitadas'}
				checked={apiEnabled}
				disabled={isFetching || isToggling || !settings}
				onChange={(event) => toggleApi(event.target.checked)}
			/>

			{apiEnabled && (
				<Button
					type="button"
					variant="blue-primary"
					onClick={() => setIsSettingsOpen(true)}
				>
					Ver configurações
				</Button>
			)}

			<Modal
				title="Configurações da integração"
				open={isSettingsOpen}
				setOpen={setIsSettingsOpen}
			>
				<Form
					form={form}
					onSubmit={handleSave}
					className="flex flex-col gap-4 p-1"
				>
					<FormHeader title="Webhook" />
					<Checkbox
						controller={{ control, name: 'webhookActive' }}
						label="Enviar atualizações de status via webhook"
					/>
					<Input
						label="URL do webhook"
						placeholder="https://cliente.com.br/webhook/beebee"
						maxLength={255}
						{...register('webhookUrl')}
						errorMessage={errors.webhookUrl?.message}
					/>
					<Input
						label="Chave do webhook"
						infoMessage="Enviada no header Authorization. Use o formato 'Header:valor' para um header customizado (ex: x-api-key:123)."
						maxLength={2000}
						{...register('authToken')}
						errorMessage={errors.authToken?.message}
					/>

					<FormHeader title="Outros" />
					<Textarea
						label="Texto customizado do SMS"
						maxLength={160}
						{...register('smsCustomText')}
					/>
					<div className="flex flex-col gap-4 md:flex-row">
						<Input
							label="ID externo da empresa"
							type="number"
							{...register('externalCompanyId')}
							errorMessage={errors.externalCompanyId?.message}
						/>
						<Input
							label="iFood ID"
							{...register('ifoodId')}
							errorMessage={errors.ifoodId?.message}
						/>
					</div>
					<Select
						label="Tabela de custo"
						controller={{ control, name: 'companyCalculatorCostId' }}
						options={calculatorCostOptions}
					/>

					<FormButtons
						onCancel={() => setIsSettingsOpen(false)}
						saveProps={{ disabled: !isDirty || isSaving }}
					/>
				</Form>
			</Modal>
		</WhiteContainer>
	);
};
